import { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import logo from "../assets/igc-logo.png";

const pdfStyles = StyleSheet.create({
  page: {
    size: "A4",
    paddingTop: 42,
    paddingBottom: 48,
    paddingHorizontal: 48,
    backgroundColor: "#FFFFFF",
    color: "#151515",
    fontFamily: "Helvetica",
  },

  header: {
    width: "100%",
    marginBottom: 18,
  },

  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  brandBlock: {
    width: "58%",
  },

  companyName: {
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 1.2,
    marginBottom: 7,
  },

  brandRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  // Width only: react-pdf computes height from the image's aspect ratio
  brandLogo: {
    width: 150,
    objectFit: "contain",
  },

  accent: {
    width: 5,
    height: 25,
    backgroundColor: "#151515",
    marginRight: 8,
  },

  brandName: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.4,
  },

  brandTag: {
    marginTop: 6,
    fontSize: 6.5,
    fontWeight: 700,
    letterSpacing: 1.5,
    color: "#707070",
  },

  companyDetails: {
    width: "38%",
    textAlign: "right",
  },

  companyDetailsText: {
    fontSize: 7.5,
    lineHeight: 1.5,
    color: "#626262",
  },

  headerRule: {
    height: 1.5,
    backgroundColor: "#151515",
    marginTop: 15,
  },

  documentIntro: {
    marginBottom: 24,
  },

  documentLabel: {
    fontSize: 6.5,
    fontWeight: 700,
    letterSpacing: 1.5,
    color: "#707070",
    marginBottom: 6,
  },

  documentTitle: {
    fontSize: 25,
    fontWeight: 700,
    letterSpacing: -0.7,
    lineHeight: 1.1,
    marginBottom: 7,
  },

  updated: {
    fontSize: 7.5,
    color: "#707070",
  },

  section: {
    marginBottom: 17,
  },

  sectionTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: 7,
    color: "#151515",
  },

  paragraph: {
    fontSize: 8.5,
    lineHeight: 1.65,
    color: "#444444",
    marginBottom: 6,
  },

  bulletList: {
    marginTop: 2,
    marginBottom: 5,
  },

  bulletRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  bullet: {
    width: 10,
    fontSize: 8.5,
    color: "#151515",
  },

  bulletText: {
    flex: 1,
    fontSize: 8.5,
    lineHeight: 1.6,
    color: "#444444",
  },

  faqQuestion: {
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: 5,
  },

  faqAnswer: {
    fontSize: 8.5,
    lineHeight: 1.65,
    color: "#444444",
  },

  footer: {
    position: "absolute",
    left: 48,
    right: 48,
    bottom: 23,
    paddingTop: 7,
    borderTopWidth: 0.7,
    borderTopColor: "#D8D8D8",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerLeft: {
    width: "60%",
  },

  footerRight: {
    width: "40%",
    textAlign: "right",
  },

  footerStrong: {
    fontSize: 6.5,
    fontWeight: 700,
    color: "#151515",
    marginBottom: 2,
  },

  footerText: {
    fontSize: 6.5,
    color: "#777777",
    lineHeight: 1.4,
  },

  pageNumber: {
    position: "absolute",
    right: 48,
    bottom: 9,
    fontSize: 6,
    color: "#999999",
  },
});

function PolicyDocument({ title, updated, sections, faqs, logoSrc }) {
  return (
    <Document
      title={`${title} - IGC Print Press`}
      author="ISO-ALPHA ECO GLOBAL CONCEPT"
      subject={title}
      creator="IGC Print Press"
    >
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <View style={pdfStyles.headerTop}>
            <View style={pdfStyles.brandBlock}>
              <Text style={pdfStyles.companyName}>
                ISO-ALPHA ECO GLOBAL CONCEPT
              </Text>

              <View style={pdfStyles.brandRow}>
                {logoSrc ? (
                  <Image src={logoSrc} style={pdfStyles.brandLogo} />
                ) : null}
              </View>

              <Text style={pdfStyles.brandTag}>
                PHC • PRINT • PACKAGING
              </Text>
            </View>

            <View style={pdfStyles.companyDetails}>
              <Text style={pdfStyles.companyDetailsText}>
                CBU Mall, Igwuruta, Along Eneka Road
              </Text>

              <Text style={pdfStyles.companyDetailsText}>
                Port Harcourt, Rivers State, Nigeria
              </Text>

              <Text style={pdfStyles.companyDetailsText}>
                +234 803 773 8028
              </Text>
            </View>
          </View>

          <View style={pdfStyles.headerRule} />
        </View>

        <View style={pdfStyles.documentIntro}>
          <Text style={pdfStyles.documentLabel}>OFFICIAL DOCUMENT</Text>

          <Text style={pdfStyles.documentTitle}>{title}</Text>

          <Text style={pdfStyles.updated}>Last updated: {updated}</Text>
        </View>

        {sections?.map((section, index) => (
          <View
            key={`${section.title}-${index}`}
            style={pdfStyles.section}
            wrap={false}
          >
            <Text style={pdfStyles.sectionTitle}>{section.title}</Text>

            {section.paragraphs?.map((paragraph, paragraphIndex) => (
              <Text
                key={`${section.title}-paragraph-${paragraphIndex}`}
                style={pdfStyles.paragraph}
              >
                {paragraph}
              </Text>
            ))}

            {section.bullets && (
              <View style={pdfStyles.bulletList}>
                {section.bullets.map((bullet, bulletIndex) => (
                  <View
                    key={`${section.title}-bullet-${bulletIndex}`}
                    style={pdfStyles.bulletRow}
                  >
                    <Text style={pdfStyles.bullet}>•</Text>

                    <Text style={pdfStyles.bulletText}>{bullet}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        ))}

        {faqs?.map((faq, index) => (
          <View key={faq.question} style={pdfStyles.section} wrap={false}>
            <Text style={pdfStyles.documentLabel}>
              {String(index + 1).padStart(2, "0")}
            </Text>

            <Text style={pdfStyles.faqQuestion}>{faq.question}</Text>

            <Text style={pdfStyles.faqAnswer}>{faq.answer}</Text>
          </View>
        ))}

        <View style={pdfStyles.footer} fixed>
          <View style={pdfStyles.footerLeft}>
            <Text style={pdfStyles.footerStrong}>
              ISO-ALPHA ECO GLOBAL CONCEPT
            </Text>

            <Text style={pdfStyles.footerText}>
              IGC Print Press • PHC • Print • Packaging
            </Text>
          </View>

          <View style={pdfStyles.footerRight}>
            <Text style={pdfStyles.footerText}>
              Port Harcourt, Rivers State, Nigeria
            </Text>

            <Text style={pdfStyles.footerText}>+234 803 773 8028</Text>
          </View>
        </View>

        <Text
          style={pdfStyles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </Page>
    </Document>
  );
}

// Convert any image source (URL, blob URL, hashed asset) to a data URI
async function toDataUrl(src) {
  if (!src) return null;
  // If it's already a data URI, return as-is
  if (typeof src === "string" && src.startsWith("data:")) return src;

  const res = await fetch(src);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function PolicyPDF({
  title,
  filename,
  updated = "September 2026",
  sections = [],
  faqs = [],
  children,
}) {
  const [logoSrc, setLogoSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;

    toDataUrl(logo)
      .then((dataUrl) => {
        if (!cancelled) setLogoSrc(dataUrl);
      })
      .catch((err) => {
        console.error("Failed to load logo for PDF:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const document = (
    <PolicyDocument
      title={title}
      updated={updated}
      sections={sections}
      faqs={faqs}
      logoSrc={logoSrc}
    />
  );

  return (
    <>
      <div className="policy-download">
        <PDFDownloadLink
          document={document}
          fileName={filename}
          className="policy-download-button"
        >
          {({ loading }) => (
            <>
              <span>↓</span>
              {loading || !logoSrc ? "Preparing PDF..." : "Download PDF"}
            </>
          )}
        </PDFDownloadLink>
      </div>

      {children}
    </>
  );
}